import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Footer } from '../components/Footer';
import { ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
const FAQS = [{
  category: 'Booking & Availability',
  items: [{
    q: 'How far in advance should I book?',
    a: 'We recommend booking 6-12 months in advance for weekend dates, especially during peak wedding season (May-October). Weekday events can often be accommodated with shorter notice.'
  }, {
    q: 'What is the booking process?',
    a: "Start with a venue tour, then we'll discuss your vision and provide a customized quote. A signed contract and deposit secure your date."
  }, {
    q: 'What is your cancellation policy?',
    a: 'Deposits are non-refundable. Cancellations made 90+ days before the event receive a 50% refund of additional payments. Please refer to your contract for full details.'
  }]
}, {
  category: 'Services & Packages',
  items: [{
    q: 'Do you provide catering?',
    a: 'Yes! Our in-house executive chef creates custom menus for every event. We accommodate all dietary restrictions and offer tastings for booked clients.'
  }, {
    q: 'Can I bring my own vendors?',
    a: 'Absolutely. While we offer preferred vendor partnerships, you are welcome to bring your own licensed and insured vendors for most services.'
  }, {
    q: "What's included in the all-inclusive package?",
    a: 'Our all-inclusive package covers venue rental, catering, basic décor, event coordination, setup and cleanup, tables, chairs, linens, and a dedicated event manager.'
  }]
}, {
  category: 'Venue Details',
  items: [{
    q: 'Is there parking available?',
    a: 'Yes, we offer complimentary parking for up to 200 vehicles. Valet parking is available as an add-on service.'
  }, {
    q: 'Is the venue wheelchair accessible?',
    a: 'Yes, H2O Event Centre is fully ADA compliant with accessible entrances, restrooms, and elevator access to all floors.'
  }, {
    q: 'What are your hours of operation?',
    a: 'Events can run until 1:00 AM. Our office is open Monday-Saturday 10AM-8PM and Sundays by appointment.'
  }]
}];
function AccordionItem({
  q,
  a
}: {
  q: string;
  a: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className={`bg-h2o-800/30 border rounded-lg overflow-hidden transition-colors duration-300 ${isOpen ? 'border-h2o-cyan/50' : 'border-slate-700/30'}`} data-id="element-367">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full flex items-center justify-between p-6 text-left focus-visible-ring" data-id="element-368">
        <span className="font-semibold text-white text-lg pr-4" data-id="element-369">{q}</span>
        <motion.div animate={{
        rotate: isOpen ? 180 : 0
      }} transition={{
        duration: 0.3
      }} data-id="element-370">
          <ChevronDown className={`w-5 h-5 ${isOpen ? 'text-h2o-cyan' : 'text-slate-400'}`} data-id="element-371" />
        </motion.div>
      </button>
      <AnimatePresence data-id="element-372">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} data-id="element-373">
            <div className="px-6 pb-6 text-slate-400 leading-relaxed" data-id="element-374">{a}</div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
export function FAQPage() {
  return <motion.main initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="min-h-screen w-full bg-h2o-950 text-slate-100 flex flex-col pt-20" data-id="element-375">
      {/* Hero Banner */}
      <section className="relative h-[40vh] min-h-[300px] flex items-center justify-center overflow-hidden" data-id="element-376">
        <div className="absolute inset-0 z-0" data-id="element-377">
          <img src="https://images.unsplash.com/photo-1505373877841-8d25f7d46678?w=1920&q=80" alt="FAQ" className="w-full h-full object-cover object-center" data-id="element-378" />
          <div className="absolute inset-0 bg-h2o-950/80" data-id="element-379" />
        </div>
        <div className="relative z-10 text-center px-6" data-id="element-380">
          <motion.h1 initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          delay: 0.2
        }} className="text-4xl md:text-6xl font-bold text-white mb-4" data-id="element-381">
            Frequently Asked Questions
          </motion.h1>
          <motion.p initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          delay: 0.3
        }} className="text-lg text-slate-300 max-w-2xl mx-auto" data-id="element-382">
            Everything you need to know about planning your event with us.
          </motion.p>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-24 px-6 md:px-12 max-w-4xl mx-auto w-full space-y-16" data-id="element-383">
        {FAQS.map((section, idx) => <motion.div key={section.category} initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: idx * 0.1
      }} data-id="element-384">
            <h2 className="text-2xl font-bold text-h2o-cyan mb-6" data-id="element-385">
              {section.category}
            </h2>
            <div className="space-y-4" data-id="element-386">
              {section.items.map((item, i) => <AccordionItem key={i} q={item.q} a={item.a} data-id="element-387" />)}
            </div>
          </motion.div>)}
      </section>

      {/* CTA */}
      <section className="py-20 px-6 bg-h2o-900 border-t border-slate-800 text-center" data-id="element-388">
        <div className="max-w-3xl mx-auto" data-id="element-389">
          <h2 className="text-3xl font-bold text-white mb-6" data-id="element-390">
            Still have questions?
          </h2>
          <p className="text-slate-400 mb-8 text-lg" data-id="element-391">
            Our team is here to help you with any specific inquiries you might
            have.
          </p>
          <Link to="/contact" className="inline-block bg-h2o-cyan text-h2o-950 font-semibold px-8 py-4 rounded-lg hover:bg-h2o-cyan-light transition-colors focus-visible-ring" data-id="element-392">
            Contact Us
          </Link>
        </div>
      </section>

      <Footer data-id="element-393" />
    </motion.main>;
}